<template>
  <div class="image-detail-editor">
    <div class="canvas-wrapper">
      <div class="imageEditorApp">
        <tui-image-editor
          v-if="currentTab === 'crop'"
          ref="editor"
          :include-ui="true"
          :options="options()"
        />
        <div
          v-else
          class="resize"
        >
          <canvas class="resize-canvas" ref="canvas"/>
          <div class="slider">
            <div class="display-size">
              <span>W :<input type="number" :value="resize.inputWidth" @input="manuallyChangeScale($event, 'width')">px</span>
              <span>H :<input type="number" :value="resize.inputHeight" @input="manuallyChangeScale($event, 'height')">px</span>
              <span>{{ resizeScalePercent }}%</span>
            </div>
            <span>10%</span>
            <input
              type="range"
              step="any"
              min="0.1"
              max="1"
              :value="resize.scale"
              @input="changeScale($event.target.value)"
            />
            <span>100%</span>
            <button class="apply" @click="resizeApply">適用</button>
          </div>
        </div>
      </div>
    </div>
    <div class="palette-wrapper">
      <button
        class="tab-change"
        @click="currentTab = 'crop'"
        :class="{ active : currentTab === 'crop' }"
      >
        切り抜き
      </button>
      <button
        class="tab-change"
        @click="currentTab = 'resize'"
        :class="{ active : currentTab === 'resize' }"
      >
        拡大・縮小
      </button>
    </div>
    <button @click="save" class="save">保存</button>
  </div>
</template>

<script>
import 'tui-image-editor/dist/tui-image-editor.css'
import 'tui-color-picker/dist/tui-color-picker.css'
import { ImageEditor } from '@toast-ui/vue-image-editor'
import Pica from 'pica'

const CANVAS_MAX_WIDTH = 1000
const CANVAS_MAX_HEIGHT = 1000
const MANUALLY_MAX_WIDTH = 5000
const MANUALLY_MAX_HEIGHT = 5000
const CROP_CSS_MAX_SIZE = 700
const CROP_SELECTION_CORNER_SIZE = 10

export default {
  components: {
    'tui-image-editor': ImageEditor
  },
  props: [
    'imageAttr',
  ],
  data () {
    return {
      isSaving: false,
      currentTab: 'crop',
      resize: {
        img: new Image(),
        ctx: null,
        scale: 1, // 倍率
        inputWidth: 1, // 入力欄 width
        inputHeight: 1, // 入力欄 height
      }
    }
  },
  computed: {
    resizeScalePercent () {
      return Math.round(this.resize.scale * 100)
    }
  },
  mounted () {
    this.initCrop()
  },
  methods: {
    options () {
      // ローテーションの点を非表示に
      window.fabric.Object.prototype.controls.mtr.offsetY = 0

      let height = this.imageAttr['data-height']
      let width = this.imageAttr['data-width']
      width = width.replace('px', '')
      height = height.replace('px', '')
      const maxSize = Math.max(width, height)
      const scale = maxSize > CROP_CSS_MAX_SIZE ? maxSize / CROP_CSS_MAX_SIZE : 1
      const cornerSize = scale * CROP_SELECTION_CORNER_SIZE
      return {
        cssMaxWidth: CROP_CSS_MAX_SIZE,
        cssMaxHeight: CROP_CSS_MAX_SIZE,
        includeUI: {
          loadImage: {
            path: this.imageAttr.src,
            name: this.imageAttr['data-filename']
          },
          initMenu: 'crop',
          menu: ['crop'],
          menuBarPosition: 'left',
        },
        selectionStyle: {
          cornerStyle: 'circle',
          cornerSize,
          cornerColor: '#fff',
          cornerStrokeColor: '#fff',
          transparentCorners: false,
          lineWidth: 2,
          borderColor: '#fff',
          rotatingPointOffset: 100,
        },
        applyCropSelectionStyle: true
      }
    },
    initResize () {
      const canvas = this.$refs.canvas
      this.resize.ctx = canvas.getContext('2d')
      const img = this.resize.img
      img.src = this.imageAttr.src
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        // Canvasを画像のサイズに合わせる
        // 比率保持
        if (img.height > CANVAS_MAX_HEIGHT || img.width > CANVAS_MAX_WIDTH) { // 画像がcanvasより大きいサイズの時
          let newWidth = CANVAS_MAX_WIDTH
          let newHeight = CANVAS_MAX_HEIGHT
          const ratio = img.width / img.height
          if (CANVAS_MAX_WIDTH / CANVAS_MAX_HEIGHT > ratio) {
            newWidth = CANVAS_MAX_HEIGHT * ratio
          } else {
            newHeight = CANVAS_MAX_WIDTH / ratio
          }
          // canvasを指定
          canvas.height = newHeight
          canvas.width = newWidth
          // スケール指定
          if (img.height > img.width) {
            this.resize.scale = canvas.height / img.height
          } else {
            this.resize.scale = canvas.width / img.width
          }
        } else { // canvasより小さい、もしくは同じサイズの時
          // canvasを指定
          canvas.height = img.height
          canvas.width = img.width
          // スケール指定
          this.resize.scale = 1
        }
        this.changeScale(this.resize.scale)
      }
    },
    manuallyChangeScale (event, type) {
      // widthとheightを手動入力された時の処理
      // バリデーションを含めた入力欄のハンドリングとスケール変更を行う
      let { scale, img } = this.resize
      let width = this.resize.inputHeight = 1
      let height = this.resize.inputHeight = 1
      if (type === 'width') {
        width = event.target.value
        if (!width) return
        if (width < 1) width = img.width * scale
        if (width > MANUALLY_MAX_WIDTH) width = MANUALLY_MAX_WIDTH
        height = width * img.height / img.width
        if (height > MANUALLY_MAX_HEIGHT) {
          height = this.resize.inputHeight = MANUALLY_MAX_HEIGHT
          width = Math.round(MANUALLY_MAX_WIDTH * img.height / img.width)
        }
      } else {
        height = event.target.value
        if (!height) return
        if (height < 1) height = img.height * scale
        if (height > MANUALLY_MAX_HEIGHT) height = MANUALLY_MAX_HEIGHT
        width = height * img.width / img.height
        if (width > MANUALLY_MAX_WIDTH) {
          width = this.resize.inputWidth = MANUALLY_MAX_WIDTH
          height = Math.round(MANUALLY_MAX_HEIGHT * img.width / img.height)
        }
      }
      if (img.height > img.width) {
        scale = height / img.height
      } else {
        scale = width / img.width
      }
      this.changeScale(scale)
    },
    changeScale (scale) {
      this.resize.scale = scale
      const { ctx, img } = this.resize
      const canvas = this.$refs.canvas
      // 一旦クリア
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      // 倍率変更
      ctx.scale(scale, scale)
      // 描画
      ctx.drawImage(img, 0, 0)
      // 使用したscaleを元に戻す
      ctx.scale(1 / scale, 1 / scale)
      // 入力欄に反映
      this.resize.inputWidth = Math.round(img.width * scale)
      this.resize.inputHeight = Math.round(img.height * scale)
    },
    resizeApply () {
      const { img, ctx, scale } = this.resize
      const canvas = this.$refs.canvas
      // 一旦クリア
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      // canvasの大きさ変更
      canvas.width = img.width * scale
      canvas.height = img.height * scale
      // 描画
      ctx.drawImage(img, 0, 0,
        img.width, img.height, 0, 0,
        img.width * scale, img.height * scale
      )
    },
    initCrop () {
      // デフォルトでpresetを選択させる処理
      // FIXME setIntervalを使わない方法があれば修正お願いします
      // this.$refs.editor.invoke('setCropzoneRect', [1, 1])はライブラリがmountされておらずエラーになる
      const rootElm = this.$refs.editor.getRootElement()
      const defaultSelect = rootElm.getElementsByClassName('preset-square')[0]
      const selectInterval = setInterval(() => {
        if (defaultSelect.classList.contains('active')) {
          clearInterval(selectInterval)
        } else {
          defaultSelect.click()
        }
      }, 1)
    },
    toBlob (dataURI) {
      const byteString = atob(dataURI.split(',')[1])
      const mimeType = dataURI.match(/(:)([a-z/]+)(;)/)[2]
      const length = byteString.length
      const content = new Uint8Array(length)
      for (let i = 0; length > i; i++) {
        content[i] = byteString.charCodeAt(i)
      }
      return new Blob([ content ], { type: mimeType })
    },
    async save () {
      if (this.isSaving) return
      this.isSaving = true
      let blob = null
      let format = 'png'
      switch (this.imageAttr['data-filetype']) {
        case 'image/jpeg':
          format = 'jpeg'
          break
        case 'image/gif':
          // png に変換
          format = 'png'
          break
      }
      if (this.currentTab === 'crop') {
        const dataURL = await this.$refs.editor.invoke('toDataURL', { format })
        blob = this.toBlob(dataURL)
      } else {
        // resizeの場合
        // 指定したサイズで切り取る
        const { img, scale } = this.resize
        const distCanvas = document.createElement('canvas')
        distCanvas.width = img.width * scale
        distCanvas.height = img.height * scale
        const pica = Pica()
        const result = await pica.resize(img, distCanvas, {
          quality: 3,
          unsharpAmount: 80,
          unsharpRadius: 0.6,
          unsharpThreshold: 2,
          alpha: true,
          transferable: true
        })
        blob = await pica.toBlob(result, `image/${format}`)
      }

      const formData = new FormData()
      formData.append('key', this.imageAttr['data-key'])
      this.$route.params.id && formData.append('contentId', this.$route.params.id)
      formData.append('file', blob, this.imageAttr['data-filename'])
      formData.append('beforeFileSize', this.imageAttr['data-filesize'])
      const json = await this.$http({
        url: '/image/update_image',
        type: 'POST',
        params: formData,
      })
      // レスポンスをstoreに保存
      if (json && json.status === 200) {
        let image = json.data.image
        image = { ...image, url: decodeURIComponent(image.url) }
        // 初期化、ImageDetailEditorを閉じる
        this.$store.commit('mergeContentImages', { [image.key]: image })
        this.$emit('reflectImage', {
          image,
          isDetailEditor: true
        })
      }
    }
  },
  watch: {
    currentTab (value) {
      this.$nextTick(() => {
        if (value === 'crop') {
          this.initCrop()
        } else {
          this.initResize()
        }
      })
    },
  }
}
</script>
<style lang="scss" scoped>
.imageEditorApp {
  width: 100%;
  height: 100%;
}
</style>
<style lang="scss">
.image-detail-editor {
  min-width: 1200px;
  height: 100%;
  width: 100%;
  position: relative;
  background-color: rgba(0, 0, 0, 0.8);
  .canvas-wrapper {
    position: absolute;
    width: 100%;
    height: 100%;
  }
  .palette-wrapper {
    z-index: 3;
    position: absolute;
    top: 10px;
    left: 15%;
    display: flex;
    button {
      user-select: none;
      cursor: pointer;
      &.tab-change {
        background: #fff;
        color: #000;
        border-radius: 2px 0 0 2px;
        opacity: 0.3;
        + .tab-change {
          border-radius: 0 2px 2px 0;
        }
        &.active {
          opacity: 1;
        }
      }
      display: block;
      width: 80px;
      padding: 5px 0;
      text-align: center;
      margin-top: 10px;
      color: #ffffff;
    }
  }
  .save {
    display: block;
    width: 80px;
    padding: 5px 0;
    text-align: center;
    margin-top: 10px;
    border-radius: 2px;
    border: 1px solid #fff;
    color: #ffffff;
    position: absolute;
    top: 10px;
    right: 10%;
    z-index: 10;
    cursor: pointer;
    user-select: none;
  }
}
.resize {
  canvas {
    background-color: #fff;
    display: block;
    position: absolute;
    top: calc(50% - 50px);
    left: 50%;
    transform: translate(-50%, -50%);
    margin: 100px auto 0;
    max-width: 100%;
    max-height: calc(100% - 100px);
    object-fit: contain;
  }
  .slider {
    position: absolute;
    top: 10px;
    right: calc(50% - 250px);
    color: #fff;
    font-weight: 300;
    width: 500px;
    text-align: center;
    input[type=number] {
      margin: 0 5px;
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
        -moz-appearance:textfield; /* Firefox */
      }
      width: 35px;
      height: 20px;
      padding: 0 5px;
      border-radius: 2px;
      color: #fff;
      text-align: center;
      border: 1px solid #ccc;
    }
    .apply {
      user-select: none;
      position: absolute;
      top: 17px;
      right: -140px;
      cursor: pointer;
      color: #fff;
      font-size: 14px;
      &::before {
        font-family: "Font Awesome 5 Free";
        font-weight: 900;
        content: '\f00c';
        padding-right: 10px;
      }
    }
    .display-size {
      margin-bottom: 10px;
      > span {
        margin: 0 10px 0 10px;
      }
    }
    input[type=range] {
      margin: 0 10px;
      vertical-align: middle;
      width: 400px;
      -webkit-appearance: none;
      appearance: none;
      background-color: #c7c7c7;
      height: 2px;
    }
    &::after {
      content: '';
      width: 1058px;
      position: absolute;
      top: 82px;
      left: calc(50% - 529px);
      color: #3c3c3c;
      border-bottom: 1px solid #3c3c3c;
    }
  }
}
.tui-image-editor-header {
  display: none;
}
.tui-image-editor-container {
  height: 100vh;
  .tui-image-editor-menu {
    > .tui-image-editor-item {
      position: relative;
      display: inline-block;
      border-radius: 2px;
      padding: 3px 8px 3px 8px;
      cursor: pointer;
      margin: 0 4px;
      &:nth-child(4) { // separator
        cursor: default;
      }
      &:nth-child(5),  // deleteButton
      &:nth-child(6),  // deleteAllButton
      &:nth-child(7),  // separator
      &:nth-child(8) { // cropbutton
        display: none;
      }
    }
  }
  .tui-image-editor-main.tui-image-editor-menu-crop {
    .tui-image-editor-submenu {
      width: 100%;
      height: 112px;
      position: absolute;
      top: -85px;
      left: 0;
      .tui-image-editor-submenu-style {
        .tui-image-editor-submenu-item {
          &.tie-crop-preset-button {
            margin: 0;
          }
        }
      }
    }
  }
  &.left {
    .tui-image-editor-submenu {
      .tui-image-editor-submenu-item {
        .tie-crop-preset-button {
          margin-right: 100px;
        }
        .tie-crop-button {
          position: absolute;
          top: 28px;
          right: 20%;
        }
        .tui-image-editor-button {
          &.preset {
            margin: 0 9px 3px 5px;
          }
        }
        .tui-image-editor-partition {
          margin: 0 auto;
        }
      }
    }
  }
  .tui-image-editor-wrap {
    z-index: 10;
  }
  .tui-image-editor-submenu {
    .tui-image-editor-submenu-item {
      .tui-image-editor-button {
        cursor: pointer;
        user-select: none;
        &.apply, &.cancel {
          position: relative;
          color: #fff;
          font-size: 14px;
          &::before {
            font-family: "Font Awesome 5 Free";
            font-weight: 900;
            background: #1b1b1b;
            padding-right: 7px;
            position: absolute;
            bottom: 6px;
            left: 0;
          }
          &::after {
            background: #1b1b1b;
            position: absolute;
            padding: 0 6px;
            bottom: 6px;
            right: -1px;
          }
        }
        &.apply {
          &::before {
            content: '\f00c';
          }
          &::after {
            content: '適用';
          }
        }
        &.cancel {
          &::before {
            content: '\f00d';
          }
          &::after {
            content: 'キャンセル';
            width: 72px;
            right: -26px;
          }
        }
      }
    }
  }
}
</style>
